import {useAppDispatch, useAppSelector} from "../../../../redux/hooks";
import useGetUrlParams from "../../../../hooks/useGetUrlParams";
import {
  selectDialogState,
  selectIsLoadingDevelopmentStageData,
  selectVehicleProgramsSelectedDevelopmentStageData,
  setIsConfigSensorReferenceFrameDialogOpen,
  setVehicleProgramsSelectedDevelopmentStageData
} from "../../../../redux/ducks/VehicleProgramsDetails/slice";
import * as React from "react";
import {useEffect, useState} from "react";
import {ReferenceFrame, updateSensorPositionsEndpoint} from "./constants";
import cloneDeep from "lodash/cloneDeep";
import {TabGraphs2d, TabGraphs3d} from "./SensorPositionTab";
import {MRT_ColumnDef, MRT_GlobalFilterTextField, MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton, MRT_ToggleGlobalFilterButton} from "material-react-table";
import {usePostRequest} from "../../../../api/helper";
import Loader from "../../../components/Loader/Loader";
import ArgosFlexbox from "../../../components/Containers/ArgosFlexbox";
import ArgosCollapseComponent from "../../../components/CollapseComponent/ArgosCollapseComponent";
import {StyledArgosDialog} from "../../../components/Dialogs/ArgosDialog";
import {ConfigSensorReferenceFrameDialog} from "../../../components/Dialogs/VehicleProgramsDetailsDialog/ConfigSensorReferenceFrameDialog";
import {Box, IconButton, Tooltip} from "@mui/material";
import {tableButtonGray, tableHeaderGray} from "../../../../constants/colors";
import LaunchIcon from "@mui/icons-material/Launch";
import {getFullSensorLocationNameGivenAbbreviation} from "../../AdasPrograms/constants";
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";

const SENSOR_POSITION_TAB_NAME = 'View Sensor Positions'

const SensorInformation = (props: any) => {
  const dispatch = useAppDispatch()
  const {developmentStageId, vehicleProgramId} = useGetUrlParams()

  /**
   * Development stage data
   */
  const loadingDevelopmentStageData = useAppSelector(selectIsLoadingDevelopmentStageData)
  const developmentStageData = useAppSelector(selectVehicleProgramsSelectedDevelopmentStageData)

  const configSensorReferenceFrameOpen = useAppSelector(state => selectDialogState(state).isConfigSensorReferenceFrameDialogOpen)

  const [globalReferenceFrame, setGlobalReferenceFrame] = useState<ReferenceFrame>({
    xAxisTransformation: 0,
    yAxisTransformation: 0,
    zAxisTransformation: 0
  })
  const [localReferenceFrame, setLocalReferenceFrame] = useState<ReferenceFrame>({
    xAxisTransformation: 0,
    yAxisTransformation: 0,
    zAxisTransformation: 0
  })
  const [originTransformation, setOriginTransformation] = useState<ReferenceFrame>({
    xAxisTransformation: 0,
    yAxisTransformation: 0,
    zAxisTransformation: 0
  })

  /**
   * Collapsible component data
   */
  useEffect(() => {
    const clonedDevelopmentStageData = cloneDeep(developmentStageData)
    const map = createCollapsibleDataMap(clonedDevelopmentStageData)
    setCollapsibleDataMap(map)
  }, [developmentStageData]);
  const [collapsibleDataMap, setCollapsibleDataMap] = React.useState<any>({})
  const createCollapsibleDataMap = (data: any) => {
    const collapsibleDataMap: any = {}

    collapsibleDataMap['Data Table'] = <DataTable data={data?.sensor_positions_table_data ?? []}/>
    collapsibleDataMap['2D View'] = <TabGraphs2d plotlyData={data}/>
    collapsibleDataMap['3D View'] = <TabGraphs3d plotlyData={data?.sensors_data_map}/>


    return collapsibleDataMap
  }

  const updateSensorPositionsHandle = usePostRequest(updateSensorPositionsEndpoint)
  const updateSensorPositions = (reqBody: any) => {
    updateSensorPositionsHandle(reqBody).then((data: any) => {
      const _developmentStageData = cloneDeep(developmentStageData)
      _developmentStageData['sensor_positions_data'] = data['sensors']
      _developmentStageData['sensor_positions_table_data'] = data['table_data']
      dispatch(setVehicleProgramsSelectedDevelopmentStageData(_developmentStageData))
    })
  }

  return (
    <div style={{height: '100%', width: '100%', overflow: 'auto'}}>
      <Loader isLoading={loadingDevelopmentStageData}>
        <ArgosFlexbox vertical style={{overflow: 'auto'}}>
          <div style={{overflow: 'auto', overflowX: 'hidden', flex: 1}}>
            <ArgosCollapseComponent
              data={collapsibleDataMap}
            />
          </div>
        </ArgosFlexbox>
      </Loader>

      <StyledArgosDialog
        width={1200}
        height={620}
        open={configSensorReferenceFrameOpen}
        onClose={() => {
          dispatch(setIsConfigSensorReferenceFrameDialogOpen(false))
        }}
      >
        <ConfigSensorReferenceFrameDialog
          vehicleProgramId={vehicleProgramId}
          developmentStageId={developmentStageId}
          globalReferenceFrame={globalReferenceFrame}
          setGlobalReferenceFrame={setGlobalReferenceFrame}
          localReferenceFrame={localReferenceFrame}
          setLocalReferenceFrame={setLocalReferenceFrame}
          originTransformation={originTransformation}
          setOriginTransformation={setOriginTransformation}
          updateSensorPositions={updateSensorPositions}
        />
      </StyledArgosDialog>
    </div>
  )
}

const DataTable = (props: any) => {
  const dispatch = useAppDispatch()
  const {data} = props
  const {developmentStageId, vehicleProgramId, adasProgramId} = useGetUrlParams()

  const sensorPositionColumns: MRT_ColumnDef<any>[] = [
    {
      id: 'sensorType',
      header: "Sensor Type",
      accessorKey: "sensorType",
    },
    {
      id: 'sensorName',
      header: "Sensor Name",
      accessorKey: "sensorName",

      Cell: ({cell, row}) => {
        const id = row?.original?.sensorId
        const url = row.original.sensor_type == "Radar" ? `/radarPerformance?id=${id}` : `/cameraPerformance?id=${id}`

        const cellValue = cell.getValue<string>()
        const val = id ? <a onClick={() => window.open(url, "_blank")} href={url} target={'_blank'}>{cellValue}</a> : cellValue

        return (
          <ArgosFlexbox style={{alignItems: "center"}}>
            {row.original.sensor_type != "IMU" && row.original.sensor_type != "Antenna" && val}
            {(row.original.sensor_type == "IMU") && cellValue}
            {(row.original.sensor_type == "Antenna") && cellValue}
          </ArgosFlexbox>
        )
      },
    },
    {
      id: 'sensorLocation',
      header: "Location",
      accessorKey: "Location Abbreviation",
      Cell: ({cell, row}) => {
        const cellValue = cell.getValue<string>()
        const tooltipText = ['IMU', 'Antenna'].includes(row.original.sensor_type) ? undefined : getFullSensorLocationNameGivenAbbreviation(cellValue)
        return (
          <Tooltip title={tooltipText} placement={'top'}>
            <div>{cellValue}</div>
          </Tooltip>

        )
      },
    },
    {
      id: 'Origin Point X',
      header: 'Origin Point X (mm)',
      accessorKey: 'Origin Point X'
    },
    {
      id: 'Origin Point Y',
      header: 'Origin Point Y (mm)',
      accessorKey: 'Origin Point Y'
    },
    {
      id: 'Origin Point Z',
      header: 'Origin Point Z (mm)',
      accessorKey: 'Origin Point Z'
    },
    {
      id: 'Yaw',
      header: 'Yaw (deg)',
      accessorKey: 'Yaw'
    },
    {
      id: 'Pitch',
      header: 'Pitch (deg)',
      accessorKey: 'Pitch'
    },
    {
      id: 'Roll',
      header: 'Roll (deg)',
      accessorKey: 'Roll'
    },
    {
      id: 'Attenuation',
      header: 'Attenuation (dB)',
      accessorKey: 'Attenuation'
    }
  ]

  const toolbarProps = {
    adasProgramId
  }

  return (
    <ArgosFlexbox style={{overflow: "auto"}} vertical>
      <ArgosTableSimple
        columns={sensorPositionColumns}
        data={data}

        enableExpanding
        enablePagination={false}
        muiExpandButtonProps={({row}) => ({
          // Show expand button only when row has sub-elements
          sx: {
            display: row.subRows?.length ? 'flex' : 'none'
          }
        })}
        getSubRows={(originalRow) => originalRow?.subRows}
        // editing
        editDisplayMode="cell"
        enableEditing
        muiTableHeadRowProps={{
          // Header row style
          sx: {
            backgroundColor: tableHeaderGray,
          },
        }}

        renderToolbarInternalActions={({table}: any) => tableToolbarActions(table, toolbarProps)}
      />
    </ArgosFlexbox>
  )
}

const tableToolbarActions = (table: any, extraProps: any) => {
  const linkToAdasPrograms = `/adas-programs-details?adasProgramId=${extraProps.adasProgramId}&htabs-1=1&htabs-1-name=Sensor%20Bill%20of%20Materials`
  return (
    <Box>
      {extraProps.adasProgramId && <Tooltip title="Go to ADAS Program" arrow placement="top">
        <IconButton
          size={'large'}
          onClick={(e) => {
            e.preventDefault()
            window.open(linkToAdasPrograms, '_blank')
          }}
        >
          <LaunchIcon/>
        </IconButton>
      </Tooltip>}
      <MRT_ToggleGlobalFilterButton table={table} style={{color: tableButtonGray}}/>
      <MRT_ToggleFiltersButton table={table} style={{color: tableButtonGray}}/>
      <MRT_ToggleFullScreenButton table={table} style={{color: tableButtonGray}}/>
      <MRT_ShowHideColumnsButton table={table} style={{color: tableButtonGray}}/>
    </Box>
  )
}

export default SensorInformation